import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../config/api';
import './OrderConfirmation.css';

const OrderConfirmation = () => {
  const { orderNumber } = useParams();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [customerEmail, setCustomerEmail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        // Check URL query parameters first (Paynow might send status in returnUrl)
        const urlStatus = searchParams.get('status');
        if (urlStatus && urlStatus.toLowerCase() === 'cancelled') {
          setOrder({ order_number: orderNumber, payment_status: 'cancelled' });
          setLoading(false);
          return;
        }

        // Check payment status from API
        const statusResponse = await api.get(`/orders/paynow/status/${orderNumber}`);
        
        // Store customer email if available
        if (statusResponse.data.customer_email) {
          setCustomerEmail(statusResponse.data.customer_email);
        }
        
        if (statusResponse.data.paid) {
          // Payment confirmed - clear cart
          // The cart will be cleared on the backend, but we can also clear it here
          setOrder({ order_number: orderNumber, payment_status: 'paid' });
          if (statusResponse.data.order?.customer_email) {
            setCustomerEmail(statusResponse.data.order.customer_email);
          }
        } else if (statusResponse.data.cancelled || statusResponse.data.status === 'cancelled') {
          // Payment was cancelled - order has been deleted, cart remains
          setOrder({ order_number: orderNumber, payment_status: 'cancelled' });
        } else {
          setOrder({ order_number: orderNumber, payment_status: 'pending' });
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        // If 404, order might have been cancelled and deleted
        if (error.response?.status === 404) {
          setOrder({ order_number: orderNumber, payment_status: 'cancelled' });
        } else {
          setOrder({ order_number: orderNumber, payment_status: 'unknown' });
        }
      } finally {
        setLoading(false);
      }
    };

    if (orderNumber) {
      checkPaymentStatus();
    }
  }, [orderNumber, searchParams]);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <>
      <Helmet>
        <title>Order Confirmation - NieHairLabel</title>
      </Helmet>
      <div className="order-confirmation">
        <div className="container">
          <div className="confirmation-content">
            {order?.payment_status === 'cancelled' ? (
              <>
                <div className="success-icon" style={{ color: '#f44336', fontSize: '4rem' }}>✗</div>
                <h1>Order Cancelled</h1>
                <p className="order-number">Order Number: <strong>{orderNumber}</strong></p>
                <p style={{ color: '#f44336', fontWeight: 'bold' }}>Payment Cancelled</p>
                <p>Your payment was cancelled. No charges have been made to your account.</p>
                <p>If you'd like to complete your order, please return to checkout and try again.</p>
              </>
            ) : (
              <>
                <div className="success-icon">✓</div>
                <h1>Order {order?.payment_status === 'paid' ? 'Confirmed!' : 'Received!'}</h1>
                <p className="order-number">Order Number: <strong>{orderNumber}</strong></p>
                {order?.payment_status === 'paid' ? (
                  <>
                    <p style={{ color: '#00aa00', fontWeight: 'bold' }}>✓ Payment Confirmed</p>
                    <p>Thank you for your order! We've received your payment and will begin processing it shortly.</p>
                    <p>You will receive an email confirmation shortly with your order details.</p>
                  </>
                ) : order?.payment_status === 'pending' ? (
                  <>
                    <p style={{ color: '#ff9900', fontWeight: 'bold' }}>⏳ Payment Pending</p>
                    <p>Your order has been received. We're waiting for payment confirmation.</p>
                    <p>If you've already made the payment, it may take a few moments to process.</p>
                  </>
                ) : (
                  <>
                    <p>Thank you for your order! We've received your order and will begin processing it shortly.</p>
                    <p>You will receive an email confirmation shortly with your order details.</p>
                  </>
                )}
                <p style={{ marginTop: '1rem', fontSize: '1.1rem' }}>
                  <strong>Save your order number!</strong> You can use it to track your order status.
                </p>
              </>
            )}
            <div className="actions">
              {order?.payment_status !== 'cancelled' && (
                <Link 
                  to={`/track-order${customerEmail ? `?orderNumber=${orderNumber}&email=${encodeURIComponent(customerEmail)}` : ''}`} 
                  className="btn btn-primary"
                >
                  Track This Order
                </Link>
              )}
              <Link to="/shop" className="btn btn-secondary">Continue Shopping</Link>
              <Link to="/" className="btn btn-outline">Back to Home</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderConfirmation;

